/**
 * A lined jacket is priced as a coat.
 *
 * The rule is not new and it is not a zipper rule. It has been in the online
 * store since 2026-10-04 (`priceGarment` in fxryshop
 * src/utils/outerwearCheckout.js), and the catalogue has carried two sets of
 * prices for far longer than that. On 2026-10-31 four services already
 * differed between the two garments:
 *
 *   SHORTEN/HEM          jacket $130/$210   coat $170/$250
 *   SHORTEN SLEEVES      jacket $130/$250   coat $170/$250
 *   TAKE IN FROM SIDES   jacket $130/$199   coat $170/$250
 *   TAPER SLEEVES        jacket $130/$199   coat $135/$199
 *
 * The counter has never asked, so every lined jacket rung up in the studio
 * was charged the unlined price: about $40 a line on a hem, a sleeve or a
 * take in. The zipper reprice on 2026-10-31 adds a fifth and much larger gap
 * ($295 against $395), which is what finally made it worth fixing.
 *
 * ⚠️ fxryshop has the same rule in src/utils/outerwearCheckout.js
 * (`priceGarment`) and src/utils/construction.js (`isLined`). Keep the three
 * in step. The online copy has more to do, because a customer at home also
 * answers whether the jacket is denim, which sends it to the Jeans tile.
 *
 * Why the garment is SWAPPED rather than the prices being looked up on the
 * side: the counter builds every screen from the garment it was handed, so a
 * jacket showing coat prices would read "Coat - Shorten/hem" on a jacket
 * screen (`shortServiceName` only strips the prefix when it matches the
 * garment). Swapping the piece to a coat is what the online store already
 * does, it needs no parallel pricing path, and everything downstream (the
 * service list, the cart, the receipt, the rack ticket) is then identical to
 * a coat that was picked as a coat in the first place.
 *
 * `lined_jacket` is kept on the piece so the record still says it arrived as
 * a jacket. Nothing prices off it; it is there so the studio and the reports
 * are not told a coat walked in.
 */

/** The garment that gets asked. Only one, deliberately: a coat stays at the
 *  coat tier whether it is lined or not, because it is the longer, heavier
 *  garment and the zip alone is 26 to 30 inches against a jacket's 20 to 24
 *  (Calley, 2026-10-31). A puffer always has a shell and a lining, so its
 *  garment IS the answer and there is nothing to ask. */
export const ASKS_LINED = "JACKET";

export const LINED_YES = "yes";
export const LINED_NO = "no";
export const LINED_NOT_SURE = "unsure";

/** What the buttons say, in order. */
export const LINED_OPTIONS = [
  { key: LINED_YES, label: "Lined" },
  { key: LINED_NO, label: "Not lined" },
  { key: LINED_NOT_SURE, label: "Not sure" },
];

const upper = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

/** Does this garment need the question? */
export const asksLined = (garment) => upper(garment) === ASKS_LINED;

/** Only "Lined" counts. "Not sure" is priced as unlined, exactly as the
 *  online store does it (construction.js), and the studio checks it on
 *  arrival and sends extra work if it has a lining. */
export const isLined = (answer) => answer === LINED_YES;

/** The garment this piece is priced and booked as. */
export const pricedAs = (garment, answer) =>
  asksLined(garment) && isLined(answer) ? "COAT" : upper(garment);

/** Has the question been answered, when it was asked at all? A jacket cannot
 *  be rung up without an answer: an unanswered jacket is how the counter has
 *  been undercharging, so leaving it blank is the one thing not allowed. */
export const linedAnswered = (garment, answer) =>
  !asksLined(garment) || [LINED_YES, LINED_NO, LINED_NOT_SURE].includes(answer);

/** What the studio reads on a jacket the counter wasn't sure about. Matches
 *  the online store's wording (construction.js NOT_SURE_LINED_NOTE). */
export const NOT_SURE_LINED_NOTE =
  "Staff weren't sure if it's lined. Booked as unlined.";

/**
 * The piece to put in the cart: the coat's catalogue item when the jacket is
 * lined, otherwise the jacket exactly as picked.
 *
 * `items` is the garment list the screen already fetched. When a lined jacket
 * is answered but COAT is somehow not in that list, the jacket is returned
 * unchanged — undercharging is better than crashing the counter mid order,
 * and `lined_jacket` still records what it was.
 */
export const pieceFor = (picked, answer, items = []) => {
  if (!picked) return picked;
  if (!asksLined(picked.name) || !isLined(answer)) {
    return asksLined(picked.name) ? { ...picked, lined_jacket: false } : picked;
  }
  const coat = items.find((item) => upper(item?.name) === "COAT");
  if (!coat) return { ...picked, lined_jacket: true };
  return { ...coat, lined_jacket: true };
};
